<template>
    <Head title="Analytics" />
    <div class="flex flex-col lg:flex-row bg-neutral-50 h-full">
        <div v-if="enabledTabs.length === 0" class="flex items-center justify-center w-full">
            <div class="text-center">
                <p class="text-gray-600">No analytics tabs are currently enabled for this organization.</p>
                <p class="text-gray-500 body-small mt-2">Please contact your administrator to enable analytics features.</p>
            </div>
        </div>
        <template v-else>
            <div class="flex flex-col lg:min-w-60 px-4 pt-6 pb-2 lg:py-16 lg:pl-11 lg:pr-2">
                <TabMenu :tabs="enabledTabs" :current-tab-path="currentTab?.path || ''" @tab-selected="setCurrentTab" />
            </div>
            <div class="px-4 py-6 lg:px-16 lg:py-16 overflow-auto w-full">
                <component :is="currentTab.component" v-if="currentTab" />
            </div>
        </template>
    </div>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { enqueueURLParamsUpdate } from "~vue/utils/enqueueURLParamsUpdate";
import { logUserInteraction } from "~vue/utils/logUtils";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import TabMenu from "~vue/ClientAnalytics/TabMenu.vue";
import ConversationThemesTab from "~vue/ClientAnalytics/tabs/ConversationThemesTab.vue";
import ImpactAnalysisTab from "~vue/ClientAnalytics/tabs/ImpactAnalysisTab.vue";
import InsightsTab from "~vue/ClientAnalytics/tabs/InsightsTab.vue";
import OverviewTab from "~vue/ClientAnalytics/tabs/OverviewTab.vue";
import { type ClientAnalyticsTabConfig, TabPath } from "~vue/ClientAnalytics/types";
import { useCustomLabels } from "~vue/composables/useCustomLabels";
import { computed, markRaw, provide, ref, watch } from "vue";

const props = defineProps<{
    tabConfig: ClientAnalyticsTabConfig;
}>();

provide("analyticsTabConfig", props.tabConfig);

const getLabel = useCustomLabels("client_analytics");

const insightsTabEnabled = computed(() => {
    return props.tabConfig.insights_attrition_enabled || props.tabConfig.insights_productivity_enabled;
});

const ALL_TABS = [
    {
        path: TabPath.Overview,
        label: "Overview",
        component: markRaw(OverviewTab),
        enabled: props.tabConfig.overview_enabled,
    },
    {
        path: TabPath.ConversationThemes,
        label: getLabel("conversation_themes"),
        component: markRaw(ConversationThemesTab),
        enabled: props.tabConfig.conversation_themes_enabled,
    },
    {
        path: TabPath.Insights,
        label: getLabel("insights"),
        badge: "Beta",
        component: markRaw(InsightsTab),
        enabled: insightsTabEnabled.value,
    },
    {
        path: TabPath.ImpactAnalysis,
        label: getLabel("impact_analysis"),
        badge: "Beta",
        component: markRaw(ImpactAnalysisTab),
        enabled: props.tabConfig.impact_analysis_enabled,
    },
] as const;

const enabledTabs = computed(() => ALL_TABS.filter((tab) => tab.enabled));

const findTabByPath = (path: string | null) => {
    if (enabledTabs.value.length === 0) {
        return null;
    }

    if (!path) {
        return enabledTabs.value[0];
    }

    const tab = enabledTabs.value.find((t) => t.path === path);
    if (tab) {
        return tab;
    }

    console.warn(`Unrecognized or disabled tab path: '${path}', falling back to first available tab`);
    return enabledTabs.value[0];
};

const currentTab = ref(findTabByPath(new URLSearchParams(window.location.search).get("tab")));

const setCurrentTab = (tabPath: TabPath) => {
    const newTab = findTabByPath(tabPath);
    if (!newTab) {
        return;
    }

    currentTab.value = newTab;

    enqueueURLParamsUpdate((params) => {
        params.set("tab", newTab.path);
        return params;
    });
};

watch(
    currentTab,
    (newTab) => {
        if (newTab) {
            logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_TAB_SHOWN, { tab: newTab.path });
        }
    },
    { immediate: true },
);
</script>
